import { t as getSegmentSessionId } from "./segment-CWTqteLr.js";
import { a as isEnvironmentLocal, c as isEnvironmentStaging, i as isEnvironmentEphemeral, o as isEnvironmentLocalAndDevelopment, s as isEnvironmentProduction } from "./Environment-B1ZpdxIS.js";
import { n as BaseApiError, t as loggerUtils } from "./Logging-BxRsYlKX.js";
import { t as appendQueryParams } from "./utils-CT_1OHHh.js";
import { camelCase, isArray, isEmpty, isPlainObject, omit, snakeCase } from "lodash-es";
import Cookies from "universal-cookie";
//#region src/Api/ApiError.ts
var ApiError = class extends BaseApiError {
	status;
	details;
	constructor(errorPayload) {
		super(errorPayload.message);
		this.name = "API Response Error";
		this.status = errorPayload.status;
		this.details = omit(errorPayload, ["message", "status"]);
	}
};
//#endregion
//#region src/Api/NetworkError.ts
/**
* Raised when a network error happens when attempting to make an API call.
*/
var NetworkError = class extends BaseApiError {};
//#endregion
//#region src/Api/api-util.ts
function addErrorMessage(error) {
	const { status, message, ...otherProps } = error;
	const uniqueValues = new Set(Object.values(otherProps).flat(Infinity));
	return {
		status,
		message: message ?? Array.from(uniqueValues).join(", "),
		...otherProps
	};
}
function buildErrorMessageColorHub(error) {
	const { status, message, ...otherProps } = error;
	const messageFromOtherProps = Object.keys(otherProps).length ? JSON.stringify(otherProps, null, 2) : "";
	return {
		status,
		message: message ?? messageFromOtherProps,
		...otherProps
	};
}
function getError(error) {
	if (error instanceof Error) return error;
	if (error instanceof Object) return new Error(JSON.stringify(error));
	return new Error(String(error));
}
/**
* Recursively converts all object keys given a converter function
* Adapted from https://stackoverflow.com/a/50620653
*/
function convertObjectKeys(object, convertKey) {
	if (isPlainObject(object)) return Object.keys(object).reduce((result, key) => {
		result[convertKey(key)] = convertObjectKeys(object[key], convertKey);
		return result;
	}, {});
	if (isArray(object)) return object.map((value) => convertObjectKeys(value, convertKey));
	return object;
}
/**
* Converts all object keys to camelCase from snake_case. This is useful for converting from Python and Javascript object syntax.
*/
function convertObjectKeysToCamelCase(object) {
	return convertObjectKeys(object, camelCase);
}
/**
* Converts all object keys to camelCase from snake_case. This is useful for converting from Javascript to Python object syntax.
*/
function convertObjectKeysToSnakeCase(object) {
	return convertObjectKeys(object, snakeCase);
}
function getCsrfCookieName() {
	if (isEnvironmentProduction()) return "csrftoken_prod";
	if (isEnvironmentStaging()) return "csrftoken_staging";
	if (isEnvironmentEphemeral()) return "csrftoken_ephemeral";
	return "csrftoken";
}
//#endregion
//#region src/Api/api.ts
var COOKIES = new Cookies();
var ENABLE_SLOW_API = isEnvironmentLocal() && false;
if (ENABLE_SLOW_API && !isEnvironmentLocal()) throw new Error("You are enabling slow api in non-dev environment!");
var NON_LOGGABLE_HTTP_ERROR_STATUSES = new Set([401]);
var X_CSRF_TOKEN_HEADER_KEY = "X-CSRFToken";
var X_SEGMENT_SESSION_ID_HEADER_KEY = "X-Segment-Session-Id";
/**
* Add default request options.
*
* @param fetchOptions - The initial fetch options.
*/
function addDefaultFetchOptions(fetchOptions, url) {
	const headers = { ...fetchOptions.headers };
	const csrfCookieName = getCsrfCookieName();
	if (!("X-CSRFToken" in headers) && COOKIES.get(csrfCookieName)) headers[X_CSRF_TOKEN_HEADER_KEY] = COOKIES.get(csrfCookieName);
	if (!("X-Segment-Session-Id" in headers)) headers[X_SEGMENT_SESSION_ID_HEADER_KEY] = getSegmentSessionId();
	let includeCredentials = isEnvironmentLocalAndDevelopment();
	const { origin } = window.location;
	let destination = "";
	try {
		destination = new URL(url).origin;
	} catch {}
	if (origin.match(/.*hub.*\.color\.(com|dev)$/) && destination.match(/.*[./]color\.(com|dev)$/)) includeCredentials = true;
	return {
		...fetchOptions,
		headers,
		credentials: includeCredentials ? "include" : "same-origin"
	};
}
var APIClient = class {
	buildErrorMessage;
	constructor(buildErrorMessage) {
		this.buildErrorMessage = buildErrorMessage;
	}
	/**
	* A fetch wrapper, with an API similar to `requests` in Python.
	*
	* Helper methods which include the method are provided below.
	*
	* @param method - The HTTP request method.
	* @param url - The url to make a request to.
	* @param options - Additional information about this request.
	*/
	request = (method, url, options = {}) => {
		let fetchHeaders = options.headers;
		const { params = {}, json = {} } = options;
		const urlWithQueryParams = appendQueryParams(url, params);
		let body;
		const methodIsGet = method === "GET";
		if (!isEmpty(json)) {
			if (!methodIsGet) body = json;
			fetchHeaders = {
				"Content-Type": "application/json",
				Accept: "application/json",
				...fetchHeaders
			};
		}
		const fetchOptions = {
			method,
			body,
			headers: fetchHeaders
		};
		return this.colorFetch(urlWithQueryParams, fetchOptions);
	};
	get = (url, options = {}) => {
		return this.request("GET", url, options);
	};
	post = (url, options = {}) => {
		return this.request("POST", url, options);
	};
	postFormData = (url, options = {}) => {
		return this.colorFetch(url, {
			...options,
			method: "POST"
		});
	};
	put = (url, options = {}) => {
		return this.request("PUT", url, options);
	};
	delete = (url, options = {}) => {
		return this.request("DELETE", url, options);
	};
	patch = (url, options = {}) => {
		return this.request("PATCH", url, options);
	};
	uploadFile = (url, options) => {
		const { params = {}, headers, method = "POST", file } = options;
		const urlWithQueryParams = appendQueryParams(url, params);
		const fetchOptions = {
			method,
			body: file,
			headers: {
				Accept: "application/json",
				...headers
			}
		};
		return this.doFetch(urlWithQueryParams, addDefaultFetchOptions(fetchOptions, url));
	};
	/**
	* A fetch wrapper.
	*
	* @param url - The url to make a request against
	* @param options - The fetch options
	*/
	colorFetch = (url, options) => {
		let fetchBody = options.body;
		if (typeof fetchBody === "object" && !(fetchBody instanceof FormData)) fetchBody = JSON.stringify(fetchBody);
		return this.doFetch(url, addDefaultFetchOptions({
			...options,
			body: fetchBody
		}, url));
	};
	doFetch = async (url, options) => {
		const UNSPECIFIED_SERVER_ERROR = {
			message: "Failed to process request.",
			status: 500
		};
		let response;
		try {
			if (ENABLE_SLOW_API) await new Promise((resolve) => setTimeout(resolve, 1e3));
			response = await fetch(url, options);
		} catch (error) {
			loggerUtils.logToConsole(getError(error));
			return Promise.reject(new NetworkError("Request failed due to a network error."));
		}
		try {
			const contentType = response.headers.get("Content-Type") || response.headers.get("content-type") || "";
			const isJsonContentType = contentType.includes("application/json");
			const { ok: responseIsOk, status } = response;
			if (!responseIsOk) {
				let responseContent = UNSPECIFIED_SERVER_ERROR;
				if (isJsonContentType) {
					const errorResponse = await response.json();
					if (typeof errorResponse === "object") responseContent = this.buildErrorMessage(errorResponse);
					else if (typeof errorResponse === "string") responseContent.message = errorResponse;
				} else if (contentType.includes("application/text")) responseContent.message = await response.text();
				responseContent.status = status;
				loggerUtils.logToConsole(/* @__PURE__ */ new Error(`Server responded with ${status} for URL: ${url}`), responseContent);
				const apiError = new ApiError(responseContent);
				if (status >= 400 && !NON_LOGGABLE_HTTP_ERROR_STATUSES.has(status)) loggerUtils.logException(apiError);
				return Promise.reject(apiError);
			}
			if (isJsonContentType) return response.json();
			return null;
		} catch (error) {
			loggerUtils.logException(getError(error));
			return Promise.reject(new ApiError(UNSPECIFIED_SERVER_ERROR));
		}
	};
};
var ApiClient = new APIClient(addErrorMessage);
var ColorHubApiClient = new APIClient(buildErrorMessageColorHub);
//#endregion
export { getCsrfCookieName as a, ApiError as c, convertObjectKeysToSnakeCase as i, ColorHubApiClient as n, getError as o, convertObjectKeysToCamelCase as r, NetworkError as s, ApiClient as t };

//# sourceMappingURL=api-DjIIBiAX.js.map